import styled from '@emotion/styled';
import { colors, fonts, radii, shadows, breakpoints } from './tokens';
import { IconButton } from './IconButton';
import { ExpandIcon, CollapseIcon } from './icons';

// Plain white card with the Figma "Floating" effect.
export const Card = styled.div`
  background: #fff;
  border-radius: ${({ radius = radii.card }) => radius}px;
  box-shadow: ${shadows.floating}, 0 0 0 1px ${colors.slate[100]};
  padding: ${({ padding = 24 }) => (typeof padding === 'number' ? `${padding}px` : padding)};
  box-sizing: border-box;
  font-family: ${fonts.family};
  color: ${colors.text};
`;

const PanelRoot = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  background: #fff;
  border-radius: ${radii.panel}px;
  box-shadow: ${shadows.panel};
  font-family: ${fonts.family};
  color: ${colors.text};
  overflow: hidden;
`;

const PanelBody = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${({ padding }) => padding};
  box-sizing: border-box;
  @media (max-width: ${breakpoints.phone - 1}px) {
    padding: ${({ padding }) => (padding === '0' ? '0' : '44px 16px 16px')};
  }
`;

const Corner = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  gap: 8px;
`;

/**
 * Panel - the Figma "Screenshare Tile": white, radius 20, strong shadow, with a small circular
 * teal expand/collapse button in the top-right corner. Holds each step's content.
 * @param expanded / onToggleExpand  wire the corner button (SessionLayout uses it to hide the video strip)
 * @param cornerExtra                extra corner buttons (e.g. the test help "?" button)
 * @param padding                    CSS padding of the body (default "40px 32px")
 */
export const Panel = ({ children, expanded, onToggleExpand, cornerExtra, padding = '40px 32px', ...rest }) => (
  <PanelRoot data-testid='session-panel' {...rest}>
    {(onToggleExpand || cornerExtra) && (
      <Corner>
        {cornerExtra}
        {onToggleExpand && (
          <IconButton label={expanded ? 'Show video feed' : 'Expand panel'} variant='teal' round size={28} onClick={onToggleExpand}>
            {expanded ? <CollapseIcon size={14} /> : <ExpandIcon size={14} />}
          </IconButton>
        )}
      </Corner>
    )}
    <PanelBody padding={padding}>{children}</PanelBody>
  </PanelRoot>
);

// Headline / body text used inside panels ("You are about to begin your eye examinations.").
export const PanelTitle = styled.h2`
  margin: 0 0 12px;
  font-family: ${fonts.family};
  font-size: ${({ small }) => (small ? 18 : 22)}px;
  line-height: 1.35;
  font-weight: 700;
  color: ${colors.heading};
  text-align: ${({ align = 'center' }) => align};
  @media (max-width: ${breakpoints.phone - 1}px) {
    font-size: 17px;
  }
`;

export const PanelText = styled.p`
  margin: 0 0 12px;
  font-family: ${fonts.family};
  font-size: ${({ small }) => (small ? 13 : 15)}px;
  line-height: 1.6;
  color: ${colors.textMuted};
  text-align: ${({ align = 'center' }) => align};
`;

// Label + read-only value field ("Collected Distance [500mm]", "Patient Device Height --").
const FieldRoot = styled.label`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  font-family: ${fonts.family};
  font-size: 12px;
  font-weight: 700;
  color: ${({ accent }) => (accent ? colors.primary : colors.textMuted)};
`;

export const FieldBox = styled.input`
  height: 44px;
  min-width: 120px;
  padding: 0 12px;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.input}px;
  background: ${({ readOnly }) => (readOnly ? colors.slate[100] : '#fff')};
  color: ${({ readOnly }) => (readOnly ? colors.slate[400] : colors.text)};
  font-family: ${fonts.family};
  font-size: 15px;
  box-sizing: border-box;
  &:focus {
    outline: 2px solid ${colors.brand[300]};
    border-color: ${colors.brand[400]};
  }
`;

export const Field = ({ label, accent, children, ...inputProps }) => (
  <FieldRoot accent={accent}>
    {label}
    {children || <FieldBox {...inputProps} />}
  </FieldRoot>
);

// Status pill ("Patient Positioned", "Calibrated Successfully").
export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: ${radii.pill}px;
  font-family: ${fonts.family};
  font-size: 12px;
  font-weight: 700;
  ${({ tone = 'success' }) =>
    ({
      success: `background:${colors.success[500]};color:#fff;`,
      warning: `background:${colors.warning[100]};color:${colors.warning[700]};`,
      error: `background:${colors.error[100]};color:${colors.error[700]};`,
      neutral: `background:${colors.slate[100]};color:${colors.slate[600]};`,
    })[tone]}
`;

export default Card;
